// Slug pipeline for ppn.gg subdomain tokens.
//
// Transforms arbitrary tenant names and titles into DNS-safe lowercase
// slugs via Unicode-aware transliteration. Falls back to a wordlist
// filler when both inputs strip to empty (pure emoji / punctuation).
//
// See docs/plans/2026-06-01_(completed)_PPN_GG_IMPLEMENTATION.html §2b.

import unidecode from 'unidecode'

/**
 * Common stopwords dropped from slug words. Tuned for tenant-name and
 * file-title content rather than general prose — keeps short legal
 * suffixes (Inc / LLC / GmbH) and prose connectors out of the URL.
 */
const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'of', 'for',
  'inc', 'llc', 'ltd', 'corp', 'co', 'gmbh', 'kk',
])

/**
 * Wordlist filler used when both tenantSlug and titleSlug strip to
 * empty. Picked deterministically from the suffix so the same suffix
 * always yields the same filler — predictable for retries.
 */
const WORDLIST_FILLER = [
  'river', 'meadow', 'canyon', 'harbor', 'forest', 'beacon',
  'quiet', 'bright', 'silver', 'gentle', 'rapid', 'amber',
  'lake', 'ridge', 'orbit', 'comet', 'cedar', 'maple',
  'ember', 'crystal', 'breeze', 'falcon', 'lotus', 'pebble',
]

/**
 * Cheap deterministic hash used only to pick a wordlist filler.
 */
function pickFiller(suffix: string): string {
  let h = 0
  for (let i = 0; i < suffix.length; i++) {
    h = (h * 31 + suffix.charCodeAt(i)) >>> 0
  }
  return WORDLIST_FILLER[h % WORDLIST_FILLER.length]!
}

/**
 * Transliterate, sanitize, and shorten `input` into a DNS-safe slug
 * bounded by `maxChars`. Empty input returns ''.
 *
 * Pipeline: unidecode → lowercase → collapse non-alphanumeric to '-'
 * → trim edges → drop stopwords → take whole words until cap.
 *
 * If the first remaining word alone exceeds `maxChars`, it is
 * hard-truncated — that case never produces a hyphen inside the slug.
 */
export function slugify(input: string, maxChars: number): string {
  if (!input || maxChars <= 0) return ''

  const ascii = unidecode(input)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  if (!ascii) return ''

  const words = ascii.split('-').filter((w) => w && !STOPWORDS.has(w))
  if (words.length === 0) return ''

  // Take whole words until the next one would overflow the cap.
  const result: string[] = []
  let len = 0
  for (const w of words) {
    const next = len + (result.length > 0 ? 1 : 0) + w.length
    if (next > maxChars) break
    result.push(w)
    len = next
  }

  // Failsafe: first word alone exceeds maxChars → hard truncate.
  if (result.length === 0) return words[0]!.slice(0, maxChars)

  return result.join('-')
}

/**
 * Compose a full subdomain slug from owner-tenant name + content title +
 * a random suffix. Caps: tenant 6 chars, title 20 chars, suffix as-is.
 *
 * Fallback chain when slugs strip empty:
 *   - both empty → `${filler}-${suffix}` (suffix-derived filler word)
 *   - tenant empty only → `${titleSlug}-${suffix}`
 *   - title empty only → `${tenantSlug}-${suffix}`
 *   - both present → `${tenantSlug}-${titleSlug}-${suffix}`
 */
export function composeSubdomainSlug(args: {
  tenantName: string
  title: string
  suffix: string
}): string {
  const tenantSlug = slugify(args.tenantName, 6)
  const titleSlug = slugify(args.title, 20)
  const parts = [tenantSlug, titleSlug, args.suffix].filter(Boolean)

  // Only the suffix made it through — emoji-only inputs etc.
  if (parts.length === 1) {
    return `${pickFiller(args.suffix)}-${args.suffix}`
  }

  return parts.join('-')
}
